import type { FunctionComponent } from 'preact';
import { useRef } from 'preact/hooks';

import { isolatedDialog } from 'Addons/fitmentSearch/isolatedKeys';
import VehicleList, { type VehicleListParams } from 'Components/fitmentSearch/vehicleList/vehicleList';
import {
  getClassesByLayoutState,
  useLayoutState,
} from 'Components/fitmentSearch/vehicleWidget/useLayoutState';
import {
  createSelectRepeater,
  type Params as SelectParams,
  SelectContextProvider,
} from 'Components/smallComponents/select';
import type { RepeaterFunctionInvoker, TemplateFunction, TemplateFunctionInvoker } from 'Components/types';
import { isolatedDiscardVehicle, replaceVehicleValue, vehicleSelected } from 'Core/actions/fitmentSearch';
import { usePopup } from 'Core/hooks/index';
import {
  createFitmentSearchResponseFacetsSelector,
  fitmentSearchBaseFieldsSelector,
  fitmentSearchExtraFieldsSelector,
  fitmentSearchFieldsSelector,
  garageEmptySelector,
  garageSizeSelector,
  isSpecifyDialogOpenedSelector,
  isVehicleSelectedSelector,
  isVehicleSelectionForcedSelector,
  selectedVehicleSelector,
} from 'Core/selectors/fitmentSearch/index';
import { useDispatch, useSelector } from 'Core/store';
import { Vehicle } from 'Models';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import { cloneSafe } from 'Utils/components';
import t from 'Utils/translate';

const dialogName = 'VehicleWidgetDialog';
const isolatedKey = isolatedDialog;
const responseFacetsSelector = createFitmentSearchResponseFacetsSelector(isolatedKey);

type Params = {
  name: string;
  close: () => void;
  garageSize: number;
  isGarageEmpty: boolean;
  vehicleList: TemplateFunctionInvoker<VehicleListParams>;
  vehicleWidget: TemplateFunctionInvoker<VehicleWidgetParams>;
};
type VehicleWidgetParams = {
  name: string;
  selects: RepeaterFunctionInvoker<SelectParams>;
  allowToDiscardVehicle: boolean;
  allowToSetVehicle: boolean;
  isSpecifyDialogOpened: boolean;
  isVehicleSelected: boolean;
  vehicle: Record<string, string>;
  vehicleBaseValues: string[];
  vehicleExtraValues: string[];
  discardVehicle: () => void;
  selectVehicle: () => void;
};

interface Props {
  template: TemplateFunction<Params>;
  columnBreakpoint?: number;
  doNotRedirectOnVehicleSelect?: boolean;
  isAutoVehicleSelectionDisabled?: boolean;
  isAlwaysColumnLayout?: boolean;
  fields?: string[];
  globalVehicleDiscardEnabled?: boolean;
  redirectUrl?: string;
  useNativeDropdown?: boolean;
  useSearchableDropdown?: boolean;
}

const VehicleWidgetDialog: FunctionComponent<Props> = ({
  template,
  columnBreakpoint,
  doNotRedirectOnVehicleSelect,
  isAutoVehicleSelectionDisabled,
  isAlwaysColumnLayout,
  fields,
  globalVehicleDiscardEnabled,
  redirectUrl,
  useNativeDropdown,
  useSearchableDropdown,
}) => {
  const rootRef = useRef<HTMLElement>(null);
  const dispatch = useDispatch();

  const { opened, close } = usePopup(dialogName);

  const isVehicleSelected = useSelector(isVehicleSelectedSelector);
  const selectedVehicle = useSelector(selectedVehicleSelector);

  const responseFacets = useSelector(responseFacetsSelector);
  const facets = fields?.length
    ? responseFacets.filter((facet) => fields.includes(facet.field))
    : responseFacets;

  const fitmentFields = useSelector(fitmentSearchFieldsSelector);
  const baseFitmentFields = useSelector(fitmentSearchBaseFieldsSelector);
  const extraFitmentFields = useSelector(fitmentSearchExtraFieldsSelector);
  const requiredFacets = fields?.length
    ? facets
    : facets.filter((facet) => baseFitmentFields.includes(facet.field));
  const isAllRequiredFieldSelected = requiredFacets.filter((f) => !f.selection.length).length === 0;

  const isSpecifyDialogOpened = useSelector(isSpecifyDialogOpenedSelector);
  const isVehicleSelectionForced = useSelector(isVehicleSelectionForcedSelector);

  const garageSize = useSelector(garageSizeSelector);
  const isGarageEmpty = useSelector(garageEmptySelector);
  const layoutState = useLayoutState(rootRef, facets, columnBreakpoint, isAlwaysColumnLayout);
  const appendedClasses = [...getClassesByLayoutState(layoutState)];

  if (!opened) {
    return null;
  }

  // if a user select a vehicle without some extra fields manually by the button,
  // mark those fields as ignored not to show the specify dialog for them
  const responseVehicle = new Vehicle(
    fitmentFields.map(
      (field) =>
        facets.selection.find((v) => v.field === field) || { field, term: '__ignored', value: '__ignored' },
    ),
    fitmentFields,
  );

  const vehicleWidget: TemplateFunctionInvoker<VehicleWidgetParams> = (templ) => {
    const selectVehicle = () => {
      if (isAllRequiredFieldSelected) {
        dispatch(
          vehicleSelected(responseVehicle, {
            doNotRedirectOnVehicleSelect,
            isSpecifyDialogOpened,
            isVehicleSelectionForced,
            isolatedKey,
            redirectUrl,
            sendIsolatedRequest: isolatedKey !== null,
            vehicleWidgetName: dialogName,
          }),
        );

        fitmentSearchConfig.onVehicleSelected(responseVehicle.filteredFieldMap);
      }
    };

    const discardVehicle = () => {
      dispatch(isolatedDiscardVehicle(isolatedKey, globalVehicleDiscardEnabled));
      if (isAllRequiredFieldSelected) {
        fitmentSearchConfig.onVehicleDiscarded();
      }
    };

    const selects =
      facets.length > 0
        ? createSelectRepeater(
            facets.map(({ field, name, facetedValues }, index) => ({
              entries: facetedValues.map((v) => ({
                value: v.value,
                term: v.term,
                payload: v.payload,
                selected: v.isSelected,
              })),
              extraClassName: 'cm_vehicle-widget_select',
              disabled: !facetedValues.length,
              hideNullOption: true,
              field,
              title: name,
              key: field,
              index,
              useNativeDropdown: !useSearchableDropdown && useNativeDropdown,
              isSearchable: useSearchableDropdown,
              onChange: (term: string) => {
                const facetValue = facetedValues.find((v) => v.term === term);
                if (facetValue) {
                  dispatch(
                    replaceVehicleValue(facetValue, isolatedKey, fitmentFields, {
                      isAutoVehicleSelectionDisabled:
                        isAutoVehicleSelectionDisabled ?? fitmentSearchConfig.isAutoVehicleSelectionDisabled,
                      isPartialMode: fields && fields?.length > 0,
                      sendIsolatedRequest: isolatedKey !== null || isAutoVehicleSelectionDisabled,
                      doNotRedirectOnVehicleSelect,
                      redirectUrl,
                      vehicleWidgetName: dialogName,
                    }),
                  );
                }
              },
            })),
          )
        : createSelectRepeater(
            baseFitmentFields.map((field, index) => {
              const title = t(`facet_title_${field.replaceAll(' ', '_')}`) || field;

              return {
                entries: [{ value: title, term: '', selected: false }],
                extraClassName: 'cm_vehicle-widget_select',
                disabled: index !== 0,
                loading: index === 0,
                hideNullOption: true,
                field,
                title,
                key: field,
                index,
                useNativeDropdown: !useSearchableDropdown && useNativeDropdown,
                isSearchable: useSearchableDropdown,
                onChange: () => {
                  /* stub */
                },
              };
            }),
          );

    const component = templ.call({
      name: dialogName,
      selects,
      allowToDiscardVehicle: facets.selection.length > 0,
      allowToSetVehicle: isAllRequiredFieldSelected,
      isSpecifyDialogOpened,
      isVehicleSelected,
      vehicle: selectedVehicle.filteredFieldMap,
      vehicleBaseValues: selectedVehicle.getFilteredValues(baseFitmentFields),
      vehicleExtraValues: selectedVehicle.getFilteredValues(extraFitmentFields),
      discardVehicle,
      selectVehicle,
    });

    return (
      <SelectContextProvider key="vehicle-widget-dialog-select-context">
        {cloneSafe(component, rootRef, { appendedClasses })}
      </SelectContextProvider>
    );
  };

  const vehicleList = (templ: TemplateFunction<VehicleListParams>) => (
    <VehicleList
      close={close}
      doNotRedirectOnVehicleSelect={doNotRedirectOnVehicleSelect}
      redirectUrl={redirectUrl}
      template={templ}
      key="vehicle-list"
    />
  );

  const component = template.call({
    name: dialogName,
    close,
    garageSize,
    isGarageEmpty,
    vehicleList,
    vehicleWidget,
  });

  return cloneSafe(component, rootRef, { appendedClasses });
};

export default VehicleWidgetDialog;
